import { createContext } from "react";

import { OrgRecord } from "@/shared/domain/orgs";
import type { OrgUserRecord } from "@/shared/domain/orgs";
import { UserRecord } from "@/shared/domain/users";
import { LoggerService } from "@/shared/services/LoggerService";
import { UserIdToken } from "@/types";

export type SignInResult =
  | { status: "signedIn" }
  | { status: "failed"; message: string }
  /** The password was right, but the account's email address was never verified. */
  | { status: "emailNotVerified"; message: string };

export type Auth = {
  initialAuthCheckComplete: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  currentUser: UserRecord | null;
  currentOrganization: OrgRecord | null;
  availableOrganizations: OrgRecord[] | null;
  /**
   * The caller's own role in each org they belong to — not the membership of
   * any one org. `null` while signed out and until the auth bootstrap
   * resolves. Reread when the current org changes, on
   * {@link Auth.refreshMyOrgRoles}, and when the window regains focus at least
   * 30 s after the last read.
   */
  myOrgRoles: OrgUserRecord[] | null;
  /**
   * Creates an account and emails it a verification code. If an unverified
   * account with this email and password already exists, sends it a new code
   * instead. Returns `null` once a code is sent, or an error message.
   */
  signUp: (emailAddress: string, password: string) => Promise<string | null>;
  ssoSignIn: (inviteId: string | null) => Promise<string | null>;
  submitVerificationCode: (
    username: string,
    code: string,
  ) => Promise<string | null>;
  signIn: (emailAddress: string, password: string) => Promise<SignInResult>;
  signOut: () => Promise<string | null>;
  forgotPasswordInitiate: (emailAddress: string) => Promise<string | null>;
  forgotPasswordComplete: (
    emailAddress: string,
    code: string,
    newPassword: string,
  ) => Promise<string | null>;
  getCurrentUser: () => Promise<UserRecord | null>;
  getCurrentOrganization: () => OrgRecord | null;
  setCurrentOrganization: (org: OrgRecord) => void;
  deleteCurrentOrganization: () => void;
  getUserIdToken: () => Promise<UserIdToken | null>;
  refreshOrgList: () => Promise<void>;
  /**
   * Reread {@link Auth.myOrgRoles}, e.g. after changing someone's role.
   * Resolves once the read settles; a failed read is reported, not thrown.
   */
  refreshMyOrgRoles: () => Promise<void>;
  resendVerificationCode: (emailAddress: string) => Promise<string | null>;
};

export const AuthContext = createContext<Auth>({
  initialAuthCheckComplete: false,
  isAuthenticated: false,
  isLoading: false,
  currentUser: null,
  currentOrganization: null,
  availableOrganizations: null,
  myOrgRoles: null,
  signUp: async () => {
    LoggerService.log("TODO: WRAP IN VALID ANALYTICS PROVIDER");
    return Promise.resolve(null);
  },
  submitVerificationCode: async () => {
    LoggerService.log("TODO: WRAP IN VALID ANALYTICS PROVIDER");
    return Promise.resolve(null);
  },
  ssoSignIn: async () => {
    LoggerService.log("TODO: WRAP IN VALID ANALYTICS PROVIDER");
    return Promise.resolve(null);
  },
  signIn: async () => {
    LoggerService.log("TODO: WRAP IN VALID ANALYTICS PROVIDER");
    return Promise.resolve({ status: "signedIn" });
  },
  signOut: async () => {
    LoggerService.log("TODO: WRAP IN VALID ANALYTICS PROVIDER");
    return Promise.resolve(null);
  },
  forgotPasswordInitiate: async () => {
    LoggerService.log("TODO: WRAP IN VALID ANALYTICS PROVIDER");
    return Promise.resolve(null);
  },
  forgotPasswordComplete: async () => {
    LoggerService.log("TODO: WRAP IN VALID ANALYTICS PROVIDER");
    return Promise.resolve(null);
  },
  getCurrentUser: async () => {
    LoggerService.log("TODO: WRAP IN VALID ANALYTICS PROVIDER");
    return Promise.resolve(null);
  },
  getCurrentOrganization: () => {
    LoggerService.log("TODO: WRAP IN VALID ANALYTICS PROVIDER");
    return null;
  },
  setCurrentOrganization: () => {
    LoggerService.log("TODO: WRAP IN VALID ANALYTICS PROVIDER");
  },
  getUserIdToken: async () => {
    LoggerService.log("TODO: WRAP IN VALID ANALYTICS PROVIDER");
    return Promise.resolve(null);
  },
  refreshMyOrgRoles: async () => {
    LoggerService.log("TODO: WRAP IN VALID ANALYTICS PROVIDER");
    return Promise.resolve();
  },
  refreshOrgList: async () => {
    LoggerService.log("TODO: WRAP IN VALID ANALYTICS PROVIDER");
    return Promise.resolve();
  },
  resendVerificationCode: async () => {
    LoggerService.log("TODO: WRAP IN VALID ANALYTICS PROVIDER");
    return Promise.resolve(null);
  },
  deleteCurrentOrganization: () => {
    LoggerService.log("TODO: WRAP IN VALID ANALYTICS PROVIDER");
  },
});
